'use client'

import { cn } from '@/lib/utils'
import { CrossCircledIcon, UpdateIcon, CheckCircledIcon, ChatBubbleIcon, ClockIcon } from '@radix-ui/react-icons'
import { ConsultationStatus, ExpertiseStatus, OrderStatus } from '@/core/graphql/types'
import React, { FC } from 'react'

type Status = ConsultationStatus | ExpertiseStatus | OrderStatus

// Keep statusConfig for colors and icons
const statusConfig: Record<string, { bgColor: string; textColor: string; icon: FC<{ className?: string }> | null }> = {
  // Shared / Consultation Statuses
  [ConsultationStatus.Active]: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircledIcon },
  [ConsultationStatus.Scheduled]: { bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: ClockIcon },
  [ConsultationStatus.Canceled]: { bgColor: 'bg-red-100', textColor: 'text-red-700', icon: CrossCircledIcon },
  [ConsultationStatus.Refused]: { bgColor: 'bg-red-100', textColor: 'text-red-700', icon: CrossCircledIcon },
  [ConsultationStatus.Completed]: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: CheckCircledIcon },

  // Expertise Statuses
  [ExpertiseStatus.Closed]: { bgColor: 'bg-gray-100', textColor: 'text-gray-700', icon: CrossCircledIcon },
  [ExpertiseStatus.Draft]: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: UpdateIcon },
  [ExpertiseStatus.Omnidoc]: { bgColor: 'bg-purple-100', textColor: 'text-purple-700', icon: ChatBubbleIcon },
  [ExpertiseStatus.Reviewed]: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircledIcon },
  [ExpertiseStatus.Sent]: { bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: ChatBubbleIcon },

  // Unique Order Statuses
  [OrderStatus.Accepted]: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircledIcon },
  [OrderStatus.ConsultationCreated]: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircledIcon },
  [OrderStatus.Pending]: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: ClockIcon },
  [OrderStatus.ExpertiseCreated]: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircledIcon },
}

const defaultConfig = { bgColor: 'bg-gray-100', textColor: 'text-gray-700', icon: ClockIcon }

interface StatusBadgeProps {
  status: Status
  statusText: string
}

export const StatusBadge = ({ status, statusText }: StatusBadgeProps) => {
  const config = status && statusConfig[status as string] ? statusConfig[status as string]! : defaultConfig
  const IconComponent = config.icon

  return (
    <span
      data-testid="consultation-status"
      className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.bgColor, config.textColor)}
    >
      {IconComponent && <IconComponent className="w-4 h-4" />}
      <span className={cn(IconComponent ? 'ml-1' : '', 'capitalize')}>{statusText}</span>
    </span>
  )
}
